import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { adminService, CreateGamePayload } from './adminService';
import { productService, ProductError } from '../product/productService';
import { Game } from '../product/types';
import { Button } from '@/components/ui/button';
import { emptyOffers, GameCommerceFields, OfferForm } from './GameCommerceFields';
import { EditionForm, GameEditionsFields } from './GameEditionsFields';

interface GameFormState extends Omit<CreateGamePayload, 'price' | 'purchaseOffers' | 'genres' | 'dateRelease' | 'completionTime' | 'listDLC'> {
  price: string;
  purchaseOffers: OfferForm[];
  genres: string;
  dateRelease: string;
  completionTime: string;
  listDLC: EditionForm[];
}

const initialFormState: GameFormState = {
  name: '',
  description: '',
  price: '',
  picture: '',
  platforms: [],
  genres: '',
  dateRelease: '',
  completionTime: '',
  online: false,
  multiplayer: false,
  screenshots: [],
  preOrder: false,
  brandNew: false,
  salesLeader: false,
  lowestPrice: false,
  topMetacritic: false,
  showcasePinned: false,
  listDLC: [],
  platformFamily: '',
  store: '',
  purchaseOffers: emptyOffers(),
};

const PLATFORM_OPTIONS = ['PS4', 'PS5', 'Xbox One', 'Xbox Series X/S', 'PC', 'Nintendo Switch', 'Battle.Net', 'Ubisoft', 'Roblox', 'PUBG', 'Minecraft'];

const mapGameToFormState = (game: Game): GameFormState => ({
  name: game.name ?? '',
  description: game.description ?? '',
  price: game.price ? String(game.price) : '',
  picture: game.picture ?? '',
  platforms: game.platforms ?? [],
  genres: game.genres?.join(', ') ?? '',
  dateRelease: game.dateRelease ? game.dateRelease.slice(0, 10) : '',
  completionTime: game.completionTime != null ? String(game.completionTime) : '',
  online: Boolean(game.online),
  multiplayer: Boolean(game.multiplayer),
  screenshots: game.screenshots ?? [],
  preOrder: Boolean(game.preOrder),
  brandNew: Boolean(game.brandNew),
  salesLeader: Boolean(game.salesLeader),
  lowestPrice: Boolean(game.lowestPrice),
  topMetacritic: Boolean(game.topMetacritic),
  showcasePinned: Boolean(game.showcasePinned),
  listDLC: (game.listDLC ?? []).map(dlc => ({
    id: dlc.id, name: dlc.name, description: dlc.description ?? '', price: String(dlc.price),
    purchaseOffers: emptyOffers().map(template => {
      const saved = dlc.purchaseOffers?.find(o => o.region === template.region && o.fulfillmentType === template.fulfillmentType && o.activationType === template.activationType);
      return saved ? { ...template, id: saved.id, price: String(saved.price) } : template;
    }),
  })),
  platformFamily: game.platformFamily ?? '',
  store: game.store ?? '',
  purchaseOffers: emptyOffers().map(template => {
    const saved = game.purchaseOffers?.find(o => o.region === template.region && o.fulfillmentType === template.fulfillmentType && o.activationType === template.activationType);
    return saved ? { ...template, id: saved.id, price: String(saved.price) } : template;
  }),
});

const buildPayload = (
  formData: GameFormState
): { payload: CreateGamePayload; error: string | null } => {
  const trimmedName = formData.name.trim();
  const trimmedDescription = formData.description.trim();
  const trimmedPicture = formData.picture.trim();
  const trimmedPrice = formData.price.trim();
  const parsedPrice = trimmedPrice === '' ? null : Number(trimmedPrice.replace(',', '.'));
  const completionTime = formData.completionTime.trim() === '' ? null : Number(formData.completionTime);
  const purchaseOffers = formData.store === 'PlayStationStore' ? formData.purchaseOffers
    .filter(o => o.price.trim() !== '')
    .map(o => ({ ...o, name: o.fulfillmentType === 'SharedAccount' ? `Шеринг — ${o.activationType === 'Included' ? 'с активацией' : 'без активации'}` : o.region!, description: '', price: Number(o.price.replace(',', '.')), isAvailable: true })) : [];
  const listDLC = formData.listDLC.map(edition => ({
    id: edition.id, name: edition.name.trim(), description: edition.description.trim(), picture: '',
    price: Number(edition.price.replace(',', '.')),
    purchaseOffers: formData.store === 'PlayStationStore' ? edition.purchaseOffers.filter(o => o.price.trim() !== '').map(o => ({ ...o, name: o.fulfillmentType === 'SharedAccount' ? `Шеринг — ${o.activationType === 'Included' ? 'с активацией' : 'без активации'}` : o.region!, description: '', price: Number(o.price.replace(',', '.')), isAvailable: true })) : [],
  }));
  if (purchaseOffers.some(o => Number.isNaN(o.price) || o.price < 0))
    return { payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], price: 0, completionTime: null }, error: 'Цены предложений должны быть числами не меньше 0.' };
  if (listDLC.some(d => !d.name || Number.isNaN(d.price) || d.price < 0 || d.purchaseOffers.some(o => Number.isNaN(o.price) || o.price < 0)))
    return { payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], price: 0, completionTime: null }, error: 'У каждого издания должны быть название и корректные цены.' };
  if (completionTime !== null && (!Number.isInteger(completionTime) || completionTime < 0))
    return { payload: { ...formData, genres: [], listDLC: [], purchaseOffers: [], price: 0, completionTime: null }, error: 'Время прохождения должно быть целым числом не меньше 0.' };

  if (!trimmedName) {
    return {
      payload: {
        ...formData,
        purchaseOffers: [],
        listDLC: [],
        genres: [],
        completionTime: null,
        name: '',
        description: trimmedDescription,
        picture: trimmedPicture,
        price: 0,
      },
      error: 'Заполни название игры.',
    };
  }

  if (parsedPrice !== null && (Number.isNaN(parsedPrice) || parsedPrice < 0)) {
    return {
      payload: {
        ...formData,
        purchaseOffers: [],
        listDLC: [],
        genres: [],
        completionTime: null,
        name: trimmedName,
        description: trimmedDescription,
        picture: trimmedPicture,
        price: 0,
      },
      error: 'Цена должна быть числом не меньше 0.',
    };
  }

  return {
    payload: {
      ...formData,
      name: trimmedName,
      description: trimmedDescription,
      picture: trimmedPicture,
      price: parsedPrice ?? 0,
      purchaseOffers,
      listDLC,
      genres: formData.genres.split(',').map(g => g.trim()).filter(Boolean),
      dateRelease: formData.dateRelease || null,
      completionTime,
    },
    error: null,
  };
};

const AdminEditProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<GameFormState>(initialFormState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [screenshotsUploading, setScreenshotsUploading] = useState(false);
  const [screenshotsError, setScreenshotsError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const screenshotsInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!id || Number.isNaN(Number(id))) return;
    try {
      setUploading(true);
      setUploadError(null);
      const url = await adminService.uploadMasterImage(Number(id), file);
      setFormData((prev) => ({ ...prev, picture: url }));
    } catch {
      setUploadError('Не удалось загрузить изображение');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleScreenshotsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    if (!id || Number.isNaN(Number(id))) return;
    try {
      setScreenshotsUploading(true);
      setScreenshotsError(null);
      const uploaded = await Promise.all(
        files.map((f) => adminService.uploadScreenshotImage(Number(id), f))
      );
      setFormData((prev) => ({
        ...prev,
        screenshots: [...prev.screenshots, ...uploaded.map((u) => u.url)],
      }));
    } catch {
      setScreenshotsError('Не удалось загрузить один или несколько скриншотов');
    } finally {
      setScreenshotsUploading(false);
      if (screenshotsInputRef.current) screenshotsInputRef.current.value = '';
    }
  };

  const removeScreenshot = (index: number) => {
    setFormData((prev) => ({ ...prev, screenshots: prev.screenshots.filter((_, i) => i !== index) }));
  };

  useEffect(() => {
    const loadGame = async () => {
      if (!id || Number.isNaN(Number(id))) {
        setError('Некорректный идентификатор игры.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await productService.fetchProduct(Number(id));
        const game = response.product as Game;
        setFormData(mapGameToFormState(game));
      } catch (err) {
        setError((err as ProductError).message || 'Не удалось загрузить карточку игры.');
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [id]);

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (event.target as HTMLInputElement).checked
          : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!id || Number.isNaN(Number(id))) {
      setSaveError('Не удалось определить игру для редактирования.');
      return;
    }

    const { payload, error: payloadError } = buildPayload(formData);

    if (payloadError) {
      setSaveError(payloadError);
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      await adminService.updateGame(Number(id), payload);
      navigate('/admin/products');
    } catch (err) {
      setSaveError((err as ProductError).message || 'Не удалось обновить игру.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || Number.isNaN(Number(id))) {
      return;
    }

    try {
      setDeleting(true);
      setSaveError(null);
      await adminService.deleteGame(Number(id));
      navigate('/admin/products');
    } catch (err) {
      setSaveError((err as ProductError).message || 'Не удалось удалить игру.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-56 animate-pulse rounded bg-gray-200" />
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
            <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
            <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
            <div className="h-12 animate-pulse rounded-xl bg-gray-100" />
          </div>
          <div className="mt-5 h-32 animate-pulse rounded-xl bg-gray-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-red-700">Не удалось открыть карточку игры</h2>
        <p className="mt-2 text-sm text-red-600">{error}</p>
        <Link
          to="/admin/products"
          className="mt-4 inline-flex items-center rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700"
        >
          Вернуться к товарам
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/admin/products"
            className="text-sm font-medium text-gray-500 transition hover:text-gray-700"
          >
            ← Назад к списку товаров
          </Link>
        </div>

        
      </div>

      {showDeleteWarning && (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-red-700">Подтвердить удаление?</h2>
          <p className="mt-2 text-sm text-red-600">
            Игра <span className="font-semibold">{formData.name || `#${id}`}</span> будет
            удалена без возможности восстановления.
          </p>

          {saveError && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-white px-4 py-3 text-sm text-red-700">
              {saveError}
            </div>
          )}

          <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              onClick={() => {
                if (!deleting) {
                  setShowDeleteWarning(false);
                  setSaveError(null);
                }
              }}
              disabled={deleting}
              variant="outline"
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Отмена
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              variant="destructive"
              className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting ? 'Удаление...' : 'Подтвердить удаление'}
            </Button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-gray-200 bg-white p-6 text-gray-900 shadow-sm sm:p-8"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Название</span>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleFieldChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Базовая цена</span>
            <input
              type="text"
              name="price"
              inputMode="decimal"
              value={formData.price}
              onChange={handleFieldChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
              placeholder="Например, 1999"
            />
            <span className="mt-1 block text-xs text-gray-400">Для PlayStation Store пересчитывается по минимальной доступной цене.</span>
          </label>

          <div className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Платформы</span>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map((p) => (
                <label key={p} className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-gray-300"
                    checked={formData.platforms.includes(p)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        platforms: e.target.checked
                          ? [...prev.platforms, p]
                          : prev.platforms.filter((x) => x !== p),
                      }))
                    }
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>

          <GameCommerceFields family={formData.platformFamily} store={formData.store} offers={formData.purchaseOffers}
            onFamilyChange={platformFamily => setFormData(prev => ({ ...prev, platformFamily }))}
            onStoreChange={store => setFormData(prev => ({ ...prev, store, ...(store === 'PlayStationStore' ? { platformFamily: 'PlayStation' as const } : {}) }))}
            onOffersChange={purchaseOffers => setFormData(prev => ({ ...prev, purchaseOffers }))} />

          <label className="block sm:col-span-2">
            <span className="mb-2 block text-sm font-medium text-gray-700">Жанры</span>
            <input name="genres" value={formData.genres} onChange={handleFieldChange} placeholder="Экшен, RPG, Приключения" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white" />
            <span className="mt-1 block text-xs text-gray-400">Укажи через запятую</span>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Дата выхода</span>
            <input type="date" name="dateRelease" value={formData.dateRelease} onChange={handleFieldChange} className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900" />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Время прохождения, ч.</span>
            <input type="number" min="0" step="1" name="completionTime" value={formData.completionTime} onChange={handleFieldChange} placeholder="Например, 25" className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900" />
          </label>

          <div className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Изображение</span>

            {/* Превью */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="relative mb-2 flex h-36 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 transition hover:border-gray-400 hover:bg-gray-100"
            >
              {formData.picture ? (
                <img
                  src={formData.picture}
                  alt="preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center gap-1 text-gray-400">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2 1.586-1.586a2 2 0 0 1 2.828 0L20 14M14 8h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
                  </svg>
                  <span className="text-xs">{uploading ? 'Загрузка...' : 'Нажмите для загрузки'}</span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <svg className="h-6 w-6 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8z" />
                  </svg>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />

            {uploadError && (
              <p className="mb-2 text-xs text-red-600">{uploadError}</p>
            )}

            {/* URL вручную */}
            <input
              type="url"
              name="picture"
              value={formData.picture}
              onChange={handleFieldChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
              placeholder="или вставьте URL изображения"
            />
          </div>
        </div>

        <label className="mt-5 block">
          <span className="mb-2 block text-sm font-medium text-gray-700">Описание</span>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleFieldChange}
            rows={6}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
            placeholder="Кратко опиши игру"
          />
        </label>

        <div className="mt-5">
          <GameEditionsFields editions={formData.listDLC} playStationStore={formData.store === 'PlayStationStore'} onChange={listDLC => setFormData(prev => ({ ...prev, listDLC }))} />
        </div>

        {/* Скриншоты */}
        <div className="mt-5">
          <span className="mb-2 block text-sm font-medium text-gray-700">Скриншоты</span>

          {formData.screenshots.length > 0 && (
            <div className="mb-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {formData.screenshots.map((url, i) => (
                <div key={i} className="group relative aspect-video overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
                  <img src={url} alt={`screenshot-${i}`} className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeScreenshot(i)}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition hover:bg-black/80 group-hover:opacity-100"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => screenshotsInputRef.current?.click()}
            disabled={screenshotsUploading}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 py-3 text-sm text-gray-400 transition hover:border-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {screenshotsUploading ? (
              <>
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v8z" />
                </svg>
                Загрузка...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                </svg>
                Добавить скриншоты
              </>
            )}
          </button>
          <input
            ref={screenshotsInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleScreenshotsChange}
          />
          {screenshotsError && (
            <p className="mt-1 text-xs text-red-600">{screenshotsError}</p>
          )}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input type="checkbox" name="online" checked={formData.online} onChange={handleFieldChange} className="h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-gray-700">Онлайн</span>
          </label>
          <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input type="checkbox" name="multiplayer" checked={formData.multiplayer} onChange={handleFieldChange} className="h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-gray-700">Вдвоём на одном экране</span>
          </label>
          <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input
              type="checkbox"
              name="preOrder"
              checked={formData.preOrder}
              onChange={handleFieldChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Предзаказ</span>
          </label>

          <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input
              type="checkbox"
              name="brandNew"
              checked={formData.brandNew}
              onChange={handleFieldChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Новинка</span>
          </label>

          <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input
              type="checkbox"
              name="salesLeader"
              checked={formData.salesLeader}
              onChange={handleFieldChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Лидер продаж</span>
          </label>

<label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input
              type="checkbox"
              name="lowestPrice"
              checked={formData.lowestPrice}
              onChange={handleFieldChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Дешевле не было</span>
          </label>

<label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input
              type="checkbox"
              name="topMetacritic"
              checked={formData.topMetacritic}
              onChange={handleFieldChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Лучшие по Metacritic</span>
          </label>

<label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <input
              type="checkbox"
              name="showcasePinned"
              checked={formData.showcasePinned}
              onChange={handleFieldChange}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Не менять витринные флаги при синхронизации Reloc</span>
          </label>
        </div>

        {saveError && !showDeleteWarning && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {saveError}
          </div>
        )}

          
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Link
            to="/admin/products"
            className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
          >
            Отмена
          </Link>

          <Button
            type="submit"
            disabled={saving || deleting}
            className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {saving ? 'Сохранение...' : 'Сохранить изменения'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminEditProductPage;
